import { t } from '!/i18n'
import { model as router } from '!/router'
import { nay } from '@/helpers'
import { logger } from '@/logger'
import { api, type LogicError } from '@setplex/tria-api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { debounce } from 'patronum/debounce'
import { AuthTab, model as tabsModel } from '~/features/change-auth-type'
import { signInByTrueIdFeatureModel } from '~/features/subscriber/sign-in'
import {
  model as signUpModel,
  modelByEmail as signUpModelByEmail,
  modelByPhone as signUpModelByPhone,
  type ViewError,
} from '~/features/subscriber/sign-up'
import { formatDateDuration } from '~/shared/helpers/date'
import { stopTimerError, updateTextError } from '../../../shared/model'
import { token } from '../../../shared/tools'

// init page
export const init = createEvent()
export const left = createEvent()
const redirectToVerifyByPhone = createEvent<router.ToEx>()

const redirectToVerifyByEmail = createEvent<router.ToEx>()

// effect to create hash token
const tokenFx = createEffect<
  { id: string; time: number; unit: string },
  string
>(({ id, time, unit }) => token(id, time, unit))

// error to show to the user
export const $error = createStore<ViewError>(null)

// reset form in case we came here not from /sign-up subpages
sample({
  clock: init,
  source: router.$previous,
  filter: (location) => nay(location?.pathname.startsWith('/sign-up')),
  target: signUpModelByEmail.signUpForm.reset,
})

// create hash token from sign up response
sample({
  clock: [
    signUpModelByEmail.signUpFx.doneData,
    signUpModelByPhone.signUpByPhoneFx.doneData,
  ],
  fn: ({ id, time_value, time_period }) => ({
    id,
    time: time_value,
    unit: time_period,
  }),
  target: tokenFx,
})

// in case we could not encode token -> log error
sample({
  clock: tokenFx.fail,
  fn: (fail) => ['could not encode token:', fail],
  target: logger.errorFx,
})

// redirect to verify page
sample({
  clock: tokenFx.doneData,
  source: signUpModelByEmail.signUpFx.done,
  fn: ({ params }, token) => ({
    pathname: `/sign-up/verify?=${AuthTab.Email}`,
    hash: token,
    state: params,
  }),
  target: redirectToVerifyByEmail,
})
sample({
  clock: redirectToVerifyByEmail,
  source: tabsModel.$selectedTab,
  filter: (tab) => tab === AuthTab.Email,
  fn: (_, path) => path,
  target: router.navigatePush,
})

sample({
  clock: tokenFx.doneData,
  source: signUpModelByPhone.signUpByPhoneFx.done,
  fn: ({ params }, token) => ({
    pathname: `/sign-up/verify?=${AuthTab.Phone}`,
    hash: token,
    state: params,
  }),
  target: redirectToVerifyByPhone,
})
sample({
  clock: redirectToVerifyByPhone,
  source: tabsModel.$selectedTab,
  filter: (tab) => tab === AuthTab.Phone,
  fn: (_, path) => path,
  target: router.navigatePush,
})

// Set right way to sign up, by default is phone but  here we need to set to email for case when `$signUpWithPhoneEnabled` is enabled
sample({
  clock: init,
  source: signUpModel.$signUpWithPhoneEnabled,
  fn: (phoneEnabled) => {
    return phoneEnabled ? AuthTab.Phone : AuthTab.Email
  },
  target: tabsModel.$selectedTab,
})

// reset forms after initialization
sample({
  clock: init,
  target: [
    signUpModelByPhone.signUpByPhoneForm.reset,
    signUpModelByEmail.signUpForm.reset,
  ],
})

//
// add listeners for server errors
//

$error
  .reset(
    signUpModelByEmail.signUpForm.$values.updates,
    init,

    // "server validation" errors
    api.events.logic.emailNotValid,
    api.events.logic.passwordNotValid,
    api.events.logic.usernameNotUnique,
    api.events.logic.subscriberUsernameRequired,
    api.events.logic.usernameNotNotMatchRE,
    api.events.logic.usernameTooLong,
    api.events.logic.subscriberPasswordRequired,
    api.events.logic.passwordTooLong,
    api.events.logic.passwordTooShort,
    api.events.logic.passwordNotMatchRE,
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.yourIPAddressLocked, // WG0018 - IP address is locked (limit exceeded)
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.inAppRegistrationDisabled, // WG0066 - In-app registration is disabled
    () => 'in_app_registration_disabled'
  )
  .on(
    api.events.logic.tooManyCodeRequests, // WG0095 - Too many new code requests
    () => 'code_request_attempt_error'
  )
  .on(
    api.events.logic.confirmationLinkSettingsNotFound, // WG0077 - Confirmation link settings not found
    () => 'confirmation_link_not_exist_error'
  )
  .on(
    api.events.logic.signUpByPhoneDisabled, // WG0094
    () => 'phone_number_sign_up_disabled_error'
  )
  // TODO: It is necessary to reconsider the approach to the implementation of error translation with additional options
  .on(
    api.events.logic.noMoreVerifyAttempts, // WG0099
    (_, { payload }: LogicError) => {
      const time = formatDateDuration({
        duration: {
          seconds: payload?.remainsSeconds,
        },
      })
      return t('invalid_sms_code_attempt_with_time_error', {
        time,
      })
    }
  )
  .on(
    api.events.logic.phoneNumberNotValid, // WG0027
    () => 'invalid_phone_number_error'
  )

  // some generic request errors
  .on(
    api.events.http.httpError, // any non-200 error
    (error) => (error === null ? 'unknown_error' : error)
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: signUpModel.$viewError,
})

sample({
  clock: updateTextError,
  fn: (data) => data.translated,
  target: signUpModel.$viewError,
})

sample({
  clock: [
    signUpModelByEmail.signUpForm.$values.updates,
    signUpModelByPhone.signUpByPhoneForm.$values.updates,
    init,
  ],
  target: stopTimerError,
})

signUpModel.$viewError.reset(
  signUpModelByEmail.signUpForm.$values.updates,
  signUpModelByPhone.signUpByPhoneForm.$values.updates,
  init
)

signInByTrueIdFeatureModel.$trueIdError.reset(left)
tabsModel.$selectedTab.reset(init)
